import { useEffect, useMemo, useState } from "react";
import { getMerchantOverview, listMovements, type WalletMovementFull, type MerchantOverview } from "@/lib/api";
import { formatAmount, formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { Smartphone } from "lucide-react";

export function StatementsPage() {
  const [overview, setOverview] = useState<MerchantOverview | null>(null);
  const [walletId, setWalletId] = useState<string>("");
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(defaultTo());
  const [movements, setMovements] = useState<WalletMovementFull[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getMerchantOverview(30).then((ov) => {
      setOverview(ov);
      if (ov.wallets.length > 0 && !walletId) {
        // Wallet_id format = "{method}_{CURRENCY}" mais overview ne renvoie que type+currency.
        // On utilise currency comme filtre principal (Ecommerce = Mobile Money only, méthode agrégée).
        setWalletId(ov.wallets[0].currency);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!walletId) return;
    setLoading(true);
    try {
      const r = await listMovements({ currency: walletId, date_from: from, date_to: to, limit: 200 });
      setMovements(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (walletId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, from, to]);

  const selected = overview?.wallets.find((w) => w.currency === walletId);
  // Exclude synthetic ledger rows (adjustment, initial_seed) from period totals
  // so summaries reflect real merchant activity only.
  const realDeposits = useMemo(
    () => movements.filter((m) => m.type === "deposit" || m.type === "withdrawal"),
    [movements],
  );
  const grossDeposits = useMemo(() => realDeposits.reduce((a, m) => a + Math.max(0, m.amount_minor), 0), [realDeposits]);
  const totalCommissions = useMemo(
    () => realDeposits.reduce((a, m) => a + (m.commission_minor ?? 0), 0),
    [realDeposits],
  );
  const netCredited = useMemo(
    () => realDeposits.reduce((a, m) => a + (m.amount_merchant_minor ?? m.amount_minor), 0),
    [realDeposits],
  );

  const columns: ColumnDef<WalletMovementFull>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 130,
      cell: (m) => <span className="text-xs text-[#3f4254]" title={formatDate(m.created_at)}>{formatRelative(m.created_at)}</span>,
    },
    {
      key: "type",
      header: "Type",
      minWidth: 170,
      cell: (m) => <Badge color={movementColor(m.type)}>{labelType(m.type)}</Badge>,
    },
    {
      key: "description",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (m) => (
        <div>
          <div className="text-sm text-[#181c32]">{m.description || labelType(m.type)}</div>
          <div className="text-xs text-[#7e8299] font-mono">{m.reference_type} · {m.reference_id.slice(0, 18)}…</div>
        </div>
      ),
    },
    {
      key: "credit",
      header: "Crédit",
      align: "right",
      minWidth: 110,
      cell: (m) =>
        m.amount_minor > 0 ? (
          <span className="font-bold text-success-700">+{formatAmount(m.amount_minor, m.currency).replace(` ${m.currency}`, "")}</span>
        ) : (
          <span className="text-[#cbd5e0]">—</span>
        ),
    },
    {
      key: "balance",
      header: "Solde après",
      align: "right",
      minWidth: 130,
      cell: (m) =>
        m.balance_after_minor != null ? (
          <span className="font-semibold text-[#181c32]" title="Solde global wallet après cette transaction (source: merchant_balance_realtime_audit)">
            {formatAmount(m.balance_after_minor, m.currency)}
          </span>
        ) : (
          <span className="text-[#cbd5e0]" title="Audit indisponible pour cette transaction (pré-fix historique)">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader title="Statements" breadcrumbs={[{ label: "Finance" }, { label: "Statements" }]} />

      <div className="card mb-5">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1 min-w-0">
              <label className="form-label">Devise</label>
              <select value={walletId} onChange={(e) => setWalletId(e.target.value)} className="form-input">
                {(overview?.wallets ?? []).map((w) => (
                  <option key={w.currency} value={w.currency}>
                    Mobile Money · {w.currency} · Solde {formatAmount(w.balance_minor, w.currency)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Du</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="form-input" />
            </div>
            <div>
              <label className="form-label">Au</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="form-input" />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => quickRange(setFrom, setTo, 30)} className="btn btn-light btn-sm">30j</button>
              <button type="button" onClick={() => quickRange(setFrom, setTo, 90)} className="btn btn-light btn-sm">90j</button>
              <button type="button" onClick={() => quickRange(setFrom, setTo, 365)} className="btn btn-light btn-sm">1 an</button>
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <KPI
              label={`Solde actuel ${selected.currency}`}
              value={formatAmount(selected.balance_minor, selected.currency)}
              icon={<Smartphone size={16} />}
              color="primary"
            />
            <KPI
              label={`Dépôts réussis (période)`}
              value={formatAmount(grossDeposits, selected.currency)}
              color="success"
            />
            <KPI
              label={`Commissions Moko (prélevées)`}
              value={formatAmount(totalCommissions, selected.currency)}
              color="danger"
            />
            <KPI
              label={`Net crédité (période)`}
              value={formatAmount(netCredited, selected.currency)}
              color="success"
            />
          </div>
          <div className="text-xs text-[#7e8299] mb-5 leading-relaxed">
            <strong>Solde actuel</strong> = balance temps réel du wallet Mobile Money {selected.currency}.
            {" "}<strong>Dépôts réussis</strong> = somme des paiements en statut <em>successful</em> sur la période (les <em>pending</em>, <em>submitted</em> et <em>failed</em> sont exclus).
            {" "}<strong>Commissions</strong> = part prélevée par Moko/telco.
            {" "}<strong>Net crédité</strong> = ce qui a effectivement atterri sur votre wallet.
            {" "}Le solde actuel peut différer du net cumulé (settlements bancaires antérieurs, historique hors période).
          </div>
        </>
      )}

      <DataTable<WalletMovementFull>
        title="Mouvements"
        subtitle={`${movements.length} mouvement${movements.length > 1 ? "s" : ""} sur la période`}
        columns={columns}
        data={movements}
        rowKey={(m) => m.id}
        loading={loading}
        emptyMessage="Aucun mouvement sur cette période"
      />
    </div>
  );
}

function defaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
function defaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}
function quickRange(setFrom: (s: string) => void, setTo: (s: string) => void, days: number) {
  const d = new Date();
  setTo(d.toISOString().slice(0, 10));
  d.setDate(d.getDate() - days);
  setFrom(d.toISOString().slice(0, 10));
}

function labelType(t: string): string {
  return (
    {
      deposit: "Dépôt",
      withdrawal: "Retrait",
      credit_payment: "Crédit paiement",
      debit_commission: "Commission",
      manual_adjustment: "Ajustement manuel",
      adjustment: "Solde d'ouverture",
      initial_seed: "Solde initial",
    } as Record<string, string>
  )[t] ?? t;
}

function movementColor(t: string): "success" | "danger" | "warning" | "info" | "light" {
  if (t === "deposit" || t.startsWith("credit_")) return "success";
  if (t === "withdrawal") return "danger";
  if (t === "adjustment" || t === "initial_seed") return "info";
  if (t === "debit_commission") return "warning";
  return "light";
}

function KPI({
  label,
  value,
  icon,
  color = "primary",
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  color?: "primary" | "success" | "danger";
}) {
  const cls = {
    primary: "text-[#181c32]",
    success: "text-success-700",
    danger: "text-danger-700",
  }[color];
  return (
    <div className="card card-body py-4">
      <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`text-lg font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
